import type { FieldErrors } from '../api/errors';
import { systemFieldRequirement } from '../domain/validation';
import { selectHeading, selectParagraph, selectTimeZone, shouldShowError } from '../state/selectors';
import { useEnv, useFormState } from './context';
import { timeFormatOf } from './format';
import { Section } from './Section';
import { SlotPicker } from './SlotPicker';

interface Props {
  errors: FieldErrors;
}

/**
 * Booking-form adapter for the shared `SlotPicker`: reads `FormState`, supplies the tenant's own
 * section copy, and leaves the picker itself free of any state-shape knowledge so the customer
 * portal can render it from `PortalState` instead.
 */
export function SlotPickerSection({ errors }: Props) {
  const { controller, now } = useEnv();
  const state = useFormState();
  const timeZone = selectTimeZone(state);

  // `service_date` is a pane-1 key (domain/validation.ts STEP1_ORDER) and this renders from
  // Step1. It used to ask about pane 2, so the "Choose a date and time" message was computed on
  // Next and then never displayed — only an API 422 surfaced it.
  const show = shouldShowError(state, 'service_date', 1)
    ? Array.from(new Set([...(errors['service_date'] ?? []), ...(state.apiErrors['service_date'] ?? [])]))
    : [];
  const dateField = systemFieldRequirement(state.bootstrap?.form ?? null, 'service_date', { requireZip: false });

  return (
    <Section heading={selectHeading(state, 'service_date_section')} paragraph={selectParagraph(state, 'service_date_information')}>
      <SlotPicker
        days={state.spots.days}
        from={state.spots.from}
        status={state.spots.status}
        loadError={state.spots.error}
        selected={state.values.slot}
        policy={state.bootstrap?.bookingPolicy ?? null}
        timeZone={timeZone}
        timeFormat={timeFormatOf(state.bootstrap?.settings ?? null)}
        now={now}
        label={dateField.label}
        required
        errors={show}
        onSelect={(slot) => controller.send({ type: 'SLOT_SELECTED', slot })}
        onLoadRange={(from) => void controller.loadSpots(from)}
      />
    </Section>
  );
}
